import { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";

import { useCurrentUsername, useUserGroups } from "../../hooks/authHooks";
import { batchUpdateMemberships } from "../../services/accountManagement.service";
import { isHttpError } from "../../services/apiService";
import { MembershipBatchResult, MembershipChange } from "../../types/accountManagement.types";

import { getMostSeniorGroup } from "./groupOrder";
import GroupPicker from "./groupPicker";

interface BulkGroupEditModalProps {
  show: boolean;
  usernames: string[];
  onClose: () => void;
  onApplied: () => void;
}

const BulkGroupEditModal: React.FC<BulkGroupEditModalProps> = ({
  show,
  usernames,
  onClose,
  onApplied,
}) => {
  const [addGroups, setAddGroups] = useState<string[]>([]);
  const [removeGroups, setRemoveGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [failures, setFailures] = useState<MembershipBatchResult[]>([]);

  const currentUsername = useCurrentUsername();
  const ownGroups = useUserGroups();
  const includesSelf =
    !!currentUsername && usernames.some((u) => u.toLowerCase() === currentUsername.toLowerCase());
  const protectedGroup = includesSelf ? getMostSeniorGroup(ownGroups) : null;

  const reset = () => {
    setAddGroups([]);
    setRemoveGroups([]);
    setErrorMessage(null);
    setFailures([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleApply = async () => {
    const changes: MembershipChange[] = usernames.flatMap((username) => [
      ...addGroups.map((group_name) => ({ username, group_name, action: "add" as const })),
      ...removeGroups.map((group_name) => ({ username, group_name, action: "remove" as const })),
    ]);

    if (changes.length === 0) {
      setErrorMessage("Select at least one group to add or remove.");
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    setFailures([]);

    try {
      const { results } = await batchUpdateMemberships(changes);
      const failed = results.filter((r) => !r.success);
      if (failed.length > 0) {
        setFailures(failed);
      } else {
        onApplied();
        handleClose();
      }
    } catch (error) {
      setErrorMessage(
        isHttpError(error)
          ? `Request failed (${String(error.response.status)}).`
          : "Request failed.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Groups for {usernames.length} User(s)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        {failures.length > 0 && (
          <Alert variant="warning">
            <p>Some changes failed:</p>
            <ul className="mb-0">
              {failures.map((f) => (
                <li key={`${f.username}-${f.group_name}-${f.action}`}>
                  {f.username} — {f.action} {f.group_name}: {f.error}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <p className="fw-bold mb-1">Add groups</p>
        <GroupPicker
          selected={addGroups}
          onChange={setAddGroups}
          disabled={submitting}
          idPrefix="bulk-add"
        />

        <p className="fw-bold mb-1 mt-3">Remove groups</p>
        <GroupPicker
          selected={removeGroups}
          onChange={setRemoveGroups}
          disabled={submitting}
          idPrefix="bulk-remove"
          lockedGroups={protectedGroup ? [protectedGroup] : []}
          lockedGroupReason="You can't remove your own most senior access group."
        />
        {protectedGroup && (
          <Form.Text className="text-muted">
            You&apos;re part of this selection, so {protectedGroup} (your most senior group)
            can&apos;t be removed in this batch.
          </Form.Text>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void handleApply()} disabled={submitting}>
          {submitting ? "Applying…" : "Apply Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkGroupEditModal;
